import type { SavedView, SavedViewsPageKey } from './types';

/**
 * localStorage adapter for saved views. Keyed per page + org so each surface has
 * its own list. Deliberately NOT the QC `qcFilter_{orgId}` key — that is QC's
 * auto-cache and would collide.
 *
 * On any storage failure (unavailable, quota, corrupt JSON) reads degrade to an
 * empty list and writes fall back to an in-memory map, so the feature never
 * throws and never crashes the page.
 */

const KEY_PREFIX = 'savedViews_';

/** Session fallback used when localStorage is unavailable or full. */
const memoryFallback = new Map<string, SavedView[]>();

export const savedViewsStorageKey = (pageKey: SavedViewsPageKey, orgId: string): string =>
  `${KEY_PREFIX}${pageKey}_${orgId}`;

const isValidView = (value: unknown): value is SavedView => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.name === 'string' &&
    typeof v.query === 'string' &&
    typeof v.createdAt === 'number'
  );
};

export const getSavedViews = (pageKey: SavedViewsPageKey, orgId: string): SavedView[] => {
  const key = savedViewsStorageKey(pageKey, orgId);
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // localStorage is unavailable (private mode) — use the session fallback.
    return memoryFallback.get(key) ?? [];
  }

  // Storage is readable: an absent key falls back to any in-session writes;
  // present-but-corrupt content resets to an empty list (never throws).
  if (!raw) return memoryFallback.get(key) ?? [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidView);
  } catch {
    return [];
  }
};

export const setSavedViews = (
  pageKey: SavedViewsPageKey,
  orgId: string,
  views: SavedView[],
): void => {
  const key = savedViewsStorageKey(pageKey, orgId);
  try {
    localStorage.setItem(key, JSON.stringify(views));
    // Keep the fallback in sync so a later read is consistent within the session.
    memoryFallback.set(key, views);
  } catch {
    // Quota exceeded / unavailable — keep the list for this session in memory.
    memoryFallback.set(key, views);
  }
};
